import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, momentLocalizer, type Event, type SlotInfo, type View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { AlertTriangle, ArrowLeft, CalendarClock, CalendarX2, CheckCircle2, CircleAlert, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CreateTaskDialog } from '@/features/dashboard/components/CreateTaskDialog';
import { dailyTasksService, type CreateTaskDto, type DailyTask } from '@/features/daily-tasks/services/daily-tasks.service';
import { useGlobalTaskModal } from '@/contexts/GlobalTaskModalContext';
import { Link } from '@tanstack/react-router';
import { useAuthStore } from '@/store/auth.store';
import { calendarService, type CalendarEvent } from '@/features/calendar/services/calendar.service';
import { cn } from '@/lib/utils';

const localizer = momentLocalizer(moment);

export const Route = createFileRoute('/_authenticated/task-planning-calendar')({
  component: TaskPlanningCalendarPage,
});

function TaskPlanningCalendarPage() {
  const queryClient = useQueryClient();
  const { openDailyTaskModal, openCalendarEventModal, openNotionTaskModal } = useGlobalTaskModal();
  const { user } = useAuthStore();
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(() => {
    if (typeof window === 'undefined') {
      return new Date();
    }

    const dateParam = new URLSearchParams(window.location.search).get('date');
    if (!dateParam) {
      return new Date();
    }

    const parsedDate = new Date(`${dateParam}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
      return new Date();
    }

    return parsedDate;
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<DailyTask | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['daily-tasks'],
    queryFn: () => dailyTasksService.getTasks(),
  });

  const { data: dashboardCalendarEvents = [] } = useQuery({
    queryKey: ['calendar-dashboard-events', user?.email],
    queryFn: () =>
      user?.email
        ? calendarService.getDashboardEventsByEmail(user.email)
        : calendarService.getDashboardEvents(),
    refetchInterval: 60000,
  });

  const createTaskMutation = useMutation({
    mutationFn: (dto: CreateTaskDto) => dailyTasksService.createTask(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks-by-email'] });
      setCreateOpen(false);
      setSelectedDay(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => dailyTasksService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks-by-email'] });
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    },
  });

  const completeCalendarEventMutation = useMutation({
    mutationFn: (eventId: string) => calendarService.completeEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-dashboard-events'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks-by-email'] });
    },
  });

  const getDayStart = (value: Date | string) => {
    const dateValue = new Date(value);
    dateValue.setHours(0, 0, 0, 0);
    return dateValue;
  };

  const isPast = (value: Date | string) => getDayStart(value).getTime() < getDayStart(new Date()).getTime();
  const isToday = (value: Date | string) => getDayStart(value).getTime() === getDayStart(new Date()).getTime();

  const tasksWithDueDate = useMemo(() => tasks.filter((task) => !!task.dueDate), [tasks]);

  const tasksWithoutDueDate = useMemo(
    () => tasks.filter((task) => task.status !== 'done' && !task.dueDate),
    [tasks],
  );

  const dailyTaskEvents = useMemo<Event[]>(
    () =>
      tasksWithDueDate.map((task) => ({
        id: task.id,
        title: task.title,
        start: new Date(task.dueDate!),
        end: new Date(task.dueDate!),
        allDay: false,
        resource: {
          kind: 'daily-task' as const,
          task,
        },
      })),
    [tasksWithDueDate],
  );

  const calendarTypeTaskEvents = useMemo<Event[]>(
    () =>
      tasks
        .filter(
          (task) =>
            task.type === 'calendar' &&
            task.calendarEventId &&
            task.dueDate,
        )
        .map((task) => ({
          id: `calendar-task-${task.id}`,
          title: task.title,
          start: new Date(task.dueDate!),
          end: new Date(task.dueDate!),
          allDay: false,
          resource: {
            kind: 'calendar-task' as const,
            task,
          },
        })),
    [tasks],
  );

  const externalCalendarEvents = useMemo<Event[]>(
    () =>
      dashboardCalendarEvents.map((calendarEvent) => ({
        id: `external-calendar-${calendarEvent.id}`,
        title: calendarEvent.title,
        start: new Date(calendarEvent.startTime),
        end: new Date(calendarEvent.endTime),
        allDay: !!calendarEvent.isAllDay,
        resource: {
          kind: 'external-calendar' as const,
          event: calendarEvent,
        },
      })),
    [dashboardCalendarEvents],
  );

  const calendarEvents = useMemo<Event[]>(
    () => [...dailyTaskEvents, ...calendarTypeTaskEvents, ...externalCalendarEvents],
    [dailyTaskEvents, calendarTypeTaskEvents, externalCalendarEvents],
  );

  const getInitialDueDateFromSelectedDay = () => {
    if (!selectedDay) return undefined;
    const dateWithDefaultHour = new Date(selectedDay);
    dateWithDefaultHour.setHours(9, 0, 0, 0);
    return dateWithDefaultHour.toISOString();
  };

  const handleCreateTask = async (dto: CreateTaskDto) => {
    return await createTaskMutation.mutateAsync(dto);
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    const chosenDate = Array.isArray(slot.slots) && slot.slots.length > 0 ? slot.slots[0] : slot.start;
    setSelectedDay(chosenDate);
    setCreateOpen(true);
  };

  const openTaskByType = (task: DailyTask) => {
    if (task.type === 'calendar' && task.calendarEventId) {
      openCalendarEventModal(task.calendarEventId);
      return;
    }
    if (task.type === 'notion' && task.notionRefId && task.notionLink) {
      openNotionTaskModal(task);
      return;
    }
    openDailyTaskModal(task, 'admin-board');
  };

  const handleSelectEvent = (event: Event) => {
    const resource = event.resource as
      | { kind: 'daily-task'; task: DailyTask }
      | { kind: 'calendar-task'; task: DailyTask }
      | { kind: 'external-calendar'; event: CalendarEvent }
      | undefined;

    if (!resource) return;
    if (resource.kind === 'external-calendar') {
      openCalendarEventModal(resource.event.id);
      return;
    }
    openTaskByType(resource.task);
  };

  const handleDeleteTaskClick = (task: DailyTask) => {
    if (task.type === 'calendar') {
      return;
    }
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTask = () => {
    if (!taskToDelete) return;
    deleteTaskMutation.mutate(taskToDelete.id);
  };

  const canDeleteTask = (task: DailyTask) => task.type !== 'calendar';

  const getEventClassName = (event: Event) => {
    const resource = event.resource as
      | { kind: 'daily-task'; task: DailyTask }
      | { kind: 'calendar-task'; task: DailyTask }
      | { kind: 'external-calendar'; event: CalendarEvent };

    if (resource?.kind === 'external-calendar') {
      return 'bg-indigo-600 text-white border-indigo-600';
    }

    const task = resource?.task;
    if (task?.status === 'done') {
      return 'bg-emerald-600 text-white border-emerald-600';
    }
    if (task?.priority === 'urgente') {
      return 'bg-fuchsia-600 text-white border-fuchsia-600';
    }
    if (!task?.dueDate) {
      return 'bg-emerald-600 text-white border-emerald-600';
    }
    if (isPast(task.dueDate)) {
      return 'bg-red-600 text-white border-red-600';
    }
    if (isToday(task.dueDate)) {
      return 'bg-amber-500 text-white border-amber-500';
    }
    return 'bg-sky-600 text-white border-sky-600';
  };

  return (
    <div className="h-full flex flex-col p-4 md:p-6 pt-6 md:pt-7">
      <Card className="mb-4 shadow-sm">
        <CardContent className="py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/my-dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Planejamento de Execução</h1>
            <p className="text-sm text-muted-foreground">
              Clique em um dia para criar tarefa com data prevista de finalização.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant="outline" className="gap-1"><CircleAlert className="h-3.5 w-3.5 text-red-600" /> Atrasadas</Badge>
              <Badge variant="outline" className="gap-1"><CalendarX2 className="h-3.5 w-3.5 text-amber-500" /> Dia atual não concluídas</Badge>
              <Badge variant="outline" className="gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Concluídas</Badge>
              <Badge variant="outline" className="gap-1"><AlertTriangle className="h-3.5 w-3.5 text-fuchsia-600" /> Urgentes</Badge>
              <Badge variant="outline" className="gap-1"><CalendarClock className="h-3.5 w-3.5 text-indigo-600" /> Eventos de calendário</Badge>
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setSelectedDay(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova tarefa
        </Button>
        </CardContent>
      </Card>

      <div className="flex-1 min-h-0 pt-4 flex flex-col xl:flex-row gap-4">
        <Card className="min-h-0 min-w-0 flex-1">
          <CardContent className="h-full p-3 md:p-4 overflow-x-hidden">
            {isLoading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Carregando tarefas...
              </div>
            ) : (
              <div className="h-[65vh] min-h-[520px] w-full overflow-x-auto">
                <Calendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  selectable
                  popup
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  components={{
                    event: ({ event }) => {
                      const resource = event.resource as
                        | { kind: 'daily-task'; task: DailyTask }
                        | { kind: 'calendar-task'; task: DailyTask }
                        | { kind: 'external-calendar'; event: CalendarEvent };

                      if (!resource || resource.kind === 'external-calendar') {
                        return <span className="truncate">{String(event.title)}</span>;
                      }

                      const task = resource.task;
                      const showDelete = canDeleteTask(task);

                      return (
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="truncate flex-1">{String(event.title)}</span>
                          {showDelete && (
                            <button
                              type="button"
                              className="shrink-0 rounded-sm hover:bg-black/20 p-0.5"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteTaskClick(task);
                              }}
                              title="Excluir tarefa"
                              aria-label="Excluir tarefa"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      );
                    },
                  }}
                  style={{ height: '100%' }}
                  messages={{
                    next: 'Próximo',
                    previous: 'Anterior',
                    today: 'Hoje',
                    month: 'Mês',
                    week: 'Semana',
                    day: 'Dia',
                    agenda: 'Agenda',
                  }}
                  eventPropGetter={(event) => {
                    return {
                      className: cn(getEventClassName(event), 'rounded border'),
                    };
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="xl:w-[320px] xl:flex-shrink-0">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Sem data prevista</h2>
              <Badge variant="secondary">{tasksWithoutDueDate.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Essas tarefas aparecem na execução por não terem data de previsão.
            </p>
            <div className="space-y-2 max-h-[60vh] overflow-auto pr-1">
              {tasksWithoutDueDate.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma tarefa sem data.</p>
              ) : (
                tasksWithoutDueDate.map((task) => (
                  <div
                    key={task.id}
                    className="w-full p-2 rounded-md border hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => openTaskByType(task)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {task.type === 'calendar'
                            ? 'calendário (somente concluir)'
                            : task.type}
                        </p>
                      </button>
                      <div className="flex items-center gap-1">
                        {task.type === 'calendar' ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                            onClick={async () => {
                              if (task.calendarEventId) {
                                completeCalendarEventMutation.mutate(task.calendarEventId);
                              }
                            }}
                            title="Concluir evento de calendário"
                            aria-label="Concluir evento de calendário"
                            disabled={completeCalendarEventMutation.isPending}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteTaskClick(task)}
                            title="Excluir tarefa"
                            aria-label="Excluir tarefa"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <CreateTaskDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setSelectedDay(null);
        }}
        onSubmit={handleCreateTask}
        creationContext="my-dashboard"
        initialDueDate={getInitialDueDateFromSelectedDay()}
      />

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setTaskToDelete(null);
          }
        }}
      >
        <DialogContent size="sm" busy={deleteTaskMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Excluir tarefa</DialogTitle>
            <DialogDescription>
              {`Tem certeza que deseja excluir "${taskToDelete?.title || 'esta tarefa'}"? Essa ação não pode ser desfeita.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setTaskToDelete(null);
              }}
              disabled={deleteTaskMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteTask}
              loading={deleteTaskMutation.isPending}
            >
              Excluir tarefa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
